import { Modal, Tabs } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router";
import { useCanAssessManagerSiteFinancially } from "../../hooks/useCanAssessManagerSiteFinanciallyQuery";
import { useConfiguratorComponents } from "../../hooks/useConfiguratorComponents";
import { useMissingObjectData } from "../../hooks/useMissingObjectData";
import { usePersons } from "../../hooks/usePersons";
import { useSiteCategories } from "../../hooks/useSiteCategories";
import { TabWithHighlightAndError } from "../../mantine/components/TabWithHighlightAndError/TabWithHighlightAndError";
import { useToast } from "../../mantine/notifications/useToast";
import type { BaseWizardProps } from "../../types/wizard.types";
import type { Connection } from "../../utils/backend-types";
import { ObjectName } from "../../utils/enums";
import { buildFieldNameToLabelMap } from "../../utils/react-hook-form/buildFieldNameToLabelMap";
import { HistoryTab } from "../HistoryTab/HistoryTab";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { useWizardStack } from "../WizardStack/WizardStackContext";
import { ConsumerForm } from "./ConsumerForm/ConsumerForm";
import { CONSUMER_FORM_FIELD_DATA } from "./ConsumerForm/ConsumerForm.constants";
import type { ConsumerFormData } from "./ConsumerForm/ConsumerForm.types";
import {
  CONSUMER_TAB_VALUES,
  CONSUMER_WIZARD_TABS
} from "./ConsumerWizard.constants";
import { useConsumer } from "./hooks/useConsumer";
import { useConsumerMutation } from "./hooks/useConsumerMutation";

type ConsumerWizardProps = BaseWizardProps & {
  siteId: number;
  variantId: number;
};

function ConsumerWizard({
  graphCoordinates = { x: 0, y: 0 },
  isOpen,
  siteId,
  variantId,
  onClose,
  ...modeProps
}: ConsumerWizardProps) {
  const { showToast } = useToast();
  const { depth } = useWizardStack();
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: locationState } = useLocation();
  const { createMutation, editMutation } = useConsumerMutation(siteId);

  const consumerId = modeProps.mode === "edit" ? modeProps.objectId : undefined;
  const isEditMode = modeProps.mode === "edit";

  const {
    persons,
    isLoading: isPersonsLoading,
    error: personsError
  } = usePersons({ variantId });

  const {
    data: connections,
    isLoading: isConnectionsLoading,
    error: connectionsError
  } = useConfiguratorComponents<Connection>(ObjectName.Connection, siteId);

  const { siteCategories } = useSiteCategories(projectId, {
    enabled: !!projectId
  });

  const {
    currentUserCanAssessManagerSiteFinancially,
    isLoading: isFinancialPermissionLoading,
    error: financialPermissionError
  } = useCanAssessManagerSiteFinancially();

  const {
    data: consumer,
    isLoading: isConsumerLoading,
    error: consumerError
  } = useConsumer(consumerId);

  const {
    isLoading: isMissingDataLoading,
    error: missingDataError,
    fieldsThatCouldBeMissing,
    shouldFieldBeHighlighted
  } = useMissingObjectData(consumerId, siteId);

  const isEEPlusSite =
    siteCategories?.find((site) => site.id === siteId)
      ?.is_eigenerzeugung_plus ?? false;
  const showOperationalPurpose =
    isEditMode &&
    !!consumer &&
    (isEEPlusSite || consumer.operational_purpose !== null);
  const showEligibleForTaxRelief =
    isEditMode &&
    !!consumer &&
    (isEEPlusSite ||
      consumer.eligible_for_tax_relief_for_particular_processes !== null);
  const showRegulatoryData = showOperationalPurpose || showEligibleForTaxRelief;
  const showPriceData =
    isEditMode && !!currentUserCanAssessManagerSiteFinancially;

  const [numErrors, setNumErrors] = useState(0);
  const [formHighlightedTabs, setFormHighlightedTabs] = useState<Array<string>>(
    []
  );

  useEffect(() => {
    if (!isOpen) {
      setNumErrors(0);
      setFormHighlightedTabs([]);
    }
  }, [isOpen]);

  const handleChangeNumErrors = useCallback((numErrors: number) => {
    setNumErrors(numErrors);
  }, []);

  const handleChangeNumHighlightedFields = useCallback(
    (_numHighlightedFields: number, highlightedTabs: Array<string>) => {
      setFormHighlightedTabs(highlightedTabs);
    },
    []
  );

  const visibleTabs = CONSUMER_WIZARD_TABS.filter((tab) => {
    if (tab.value === CONSUMER_TAB_VALUES.REGULATORY_DATA) {
      return showRegulatoryData;
    }

    if (tab.value === CONSUMER_TAB_VALUES.PRICE_DATA) {
      return showPriceData;
    }

    return true;
  });

  function getActiveTab() {
    const requestedTab = searchParams.get("tab") ?? CONSUMER_TAB_VALUES.GENERAL;
    const requestedTabIsVisible = visibleTabs.some(
      (tab) => tab.value === requestedTab
    );

    return requestedTabIsVisible ? requestedTab : CONSUMER_TAB_VALUES.GENERAL;
  }

  const activeTab = getActiveTab();

  async function handleSubmitCreate(formData: ConsumerFormData) {
    return createMutation.mutateAsync(
      {
        ...formData,
        // `person` is a required select, so it is always set on a valid submit.
        person: formData.person as number,
        site: siteId,
        sketch_element: {
          x_position: graphCoordinates.x,
          y_position: graphCoordinates.y
        }
      },
      {
        onSuccess: () => {
          onClose();
          showToast({
            type: "success",
            message: "Der Verbraucher wurde erstellt."
          });
        }
      }
    );
  }

  function handleSubmitUpdate(formData: ConsumerFormData, id: number) {
    return editMutation.mutateAsync(
      { ...formData, id, person: formData.person ?? undefined },
      {
        onSuccess: () => {
          onClose();
          showToast({
            type: "success",
            message: "Die Änderungen wurden gespeichert."
          });
        }
      }
    );
  }

  const fieldNameToLabelMap = buildFieldNameToLabelMap<ConsumerFormData>(
    CONSUMER_FORM_FIELD_DATA
  );

  const isLoading =
    isPersonsLoading ||
    isConnectionsLoading ||
    (isEditMode &&
      (isConsumerLoading ||
        isMissingDataLoading ||
        isFinancialPermissionLoading));
  const error =
    personsError ||
    connectionsError ||
    (isEditMode
      ? consumerError || missingDataError || financialPermissionError
      : null);

  const title = consumer?.name
    ? `${consumer.name} bearbeiten`
    : isEditMode
      ? "Verbraucher bearbeiten"
      : "Verbraucher erstellen";

  const formModeProps =
    modeProps.mode === "create"
      ? { mode: "create" as const }
      : consumer
        ? {
            mode: "edit" as const,
            wizardObject: consumer,
            fieldsThatCouldBeMissing,
            shouldFieldBeHighlighted
          }
        : null;

  function renderForm() {
    if (!persons || !connections || !formModeProps) {
      return null;
    }

    return (
      <ConsumerForm
        connections={connections}
        persons={persons}
        showEligibleForTaxRelief={showEligibleForTaxRelief}
        showFooter={activeTab !== CONSUMER_TAB_VALUES.HISTORY}
        showOperationalPurpose={showOperationalPurpose}
        showPriceData={showPriceData}
        onCancel={onClose}
        onChangeNumErrors={handleChangeNumErrors}
        onChangeNumHighlightedFields={handleChangeNumHighlightedFields}
        onSubmitCreate={handleSubmitCreate}
        onSubmitUpdate={handleSubmitUpdate}
        {...formModeProps}
      />
    );
  }

  return (
    <Modal
      className="ConsumerWizard"
      closeOnEscape={depth === 0}
      opened={isOpen}
      size="xl"
      title={title}
      onClose={onClose}
    >
      <LoadOrError error={error} loading={isLoading}>
        {isEditMode ? (
          <Tabs
            value={activeTab}
            onChange={(newTab) => {
              if (!newTab) {
                return;
              }

              searchParams.set("tab", newTab);
              // Thread `location.state` through the tab change so the
              // `location.state`-gated Regulatorische Daten highlight survives a
              // switch to a tab other than the landing one.
              setSearchParams(searchParams, { state: locationState });
            }}
          >
            <Tabs.List>
              {visibleTabs.map((tab, index) => (
                <TabWithHighlightAndError
                  component="a"
                  highlighted={formHighlightedTabs.includes(tab.value)}
                  key={tab.value}
                  numErrors={index === 0 ? numErrors : 0}
                  value={tab.value}
                >
                  {tab.label}
                </TabWithHighlightAndError>
              ))}
            </Tabs.List>
            {renderForm()}
            {consumerId && (
              <Tabs.Panel value={CONSUMER_TAB_VALUES.HISTORY}>
                <HistoryTab
                  entityId={consumerId}
                  entityType={ObjectName.Consumer}
                  fieldNameToLabelMap={{
                    ...fieldNameToLabelMap,
                    operation_periods: "Unternehmen"
                  }}
                />
              </Tabs.Panel>
            )}
          </Tabs>
        ) : (
          renderForm()
        )}
      </LoadOrError>
    </Modal>
  );
}

export { ConsumerWizard, ConsumerWizardProps };
